/** @format */

import {
	COMPARE_PRODUCT_LIST_HIDE_FEATURE_SESSION_STORAGE_KEY,
	COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY
} from '@plp/data-access/constants';
import {
	CompareBarProduct,
	CompareItemAttribute,
	CompareProductDetails,
	CompareProductItem
} from '@shared/data-access/interfaces';
import {
	ComponentRef,
	inject,
	Inject,
	Injectable,
	Injector,
	PLATFORM_ID,
	runInInjectionContext,
	TemplateRef
} from '@angular/core';
import { Store } from '@ngrx/store';
import { SessionStorageService } from './session-storage.service';
import { isPlatformServer } from '@angular/common';
import { BehaviorSubject, Subject, take } from 'rxjs';
import { ProductListCompareDao } from './product-list-compare/product-list-compare.dao';
import {
	setCompareProductDetails,
	setCompareSkeletonLoader,
	setCompareProductDetailsCallFailed,
	setCompareBarProducts,
	removeCompareProduct,
	addCompareProduct,
	setIsCompareFeatureHidden,
	setIsCompareGridOpen
} from '@plp/data-access/state/compare/compare.actions';
import { HmfDrawerComponent, HmfModalFactoryService } from '@dsg-tech/homefield-angular';
import { DeviceService } from '@shared/utils/services';
import {
	selectCompareProductDetails,
	selectCompareBarProducts,
	selectIsCompareGridOpen
} from '@plp/data-access/state/compare/compare.selectors';
import { BreakPointNew } from '@shared/data-access/enums';

@Injectable({
	providedIn: 'root'
})
export class ProductListCompareService {
	private drawerRef: ComponentRef<HmfDrawerComponent> | null = null;
	private drawerContentRef: TemplateRef<unknown> | null = null;
	private modalFactoryService;
	private readonly paintedDoorTest = new Subject<void>();

	readonly paintedDoorTest$ = this.paintedDoorTest.asObservable();
	readonly readyToInit = new BehaviorSubject(false);
	readonly compareBarList$ = this.store.selectSignal(selectCompareBarProducts);
	readonly compareGridList = this.store.selectSignal(selectCompareProductDetails);
	readonly isCompareGridOpen = this.store.selectSignal(selectIsCompareGridOpen);

	constructor(
		private readonly store: Store,
		private readonly deviceService: DeviceService,
		private readonly sessionStorageService: SessionStorageService,
		private readonly productListCompareDao: ProductListCompareDao,
		private readonly injector: Injector,
		@Inject(PLATFORM_ID) private readonly platformId: string
	) {}

	initCompareGrid(drawerContentRef: TemplateRef<unknown>): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}
		this.drawerContentRef = drawerContentRef;
		this.readyToInit.subscribe((ready) => {
			if (ready && !this.modalFactoryService) {
				runInInjectionContext(this.injector, () => {
					this.modalFactoryService = inject(HmfModalFactoryService);
				});
			}
		});
	}

	addProductToCompareList(compareBarProduct: CompareBarProduct): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.store.dispatch(addCompareProduct({ compareBarProduct }));
	}

	removeProductFromCompareList(ecode: string): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.store.dispatch(removeCompareProduct({ ecode }));
	}

	getCompareListFromSessionStorage(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		const compareBarProducts: CompareBarProduct[] =
			JSON.parse(
				this.sessionStorageService.get(COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY, '')
			) || [];

		this.store.dispatch(setCompareBarProducts({ compareBarProducts }));
	}

	updateSessionStorage(compareProducts: CompareBarProduct[]): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.sessionStorageService.set(
			COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY,
			JSON.stringify(compareProducts),
			''
		);
	}

	clearCompareList(): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}

		this.sessionStorageService.remove(COMPARE_PRODUCT_LIST_SESSION_STORAGE_KEY, '');
		this.store.dispatch(setCompareBarProducts({ compareBarProducts: [] }));
	}

	openPaintedDoorModal(): void {
		this.paintedDoorTest.next();
		this.sessionStorageService.set(
			COMPARE_PRODUCT_LIST_HIDE_FEATURE_SESSION_STORAGE_KEY,
			'true',
			''
		);
	}

	openCompareGrid(): void {
		const deviceType = this.deviceService.getDeviceType();

		if (!this.drawerRef) {
			this.drawerRef = this.modalFactoryService.create(
				HmfDrawerComponent,
				{
					direction: deviceType === 'desktop' ? 'right' : 'bottom',
					mobilePeek: deviceType !== 'desktop',
					size: this.setDeviceTypeAndDrawerWidth(),
					focus: false,
					closable: true,
					closableSide: 'right',
					isCloseButtonSticky: true,
					closableVariant: 'primary',
					headerText: 'Product Comparison'
				},
				this.drawerContentRef
			);
		}

		this.drawerRef.instance.closed.pipe(take(1)).subscribe(() => {
			this.store.dispatch(setIsCompareGridOpen({ isCompareGridOpen: false }));
		});

		this.updateDrawerSettings();
		this.drawerRef.instance.openDrawer();
		this.store.dispatch(setIsCompareGridOpen({ isCompareGridOpen: true }));
	}

	closeCompareGrid(): void {
		if (!this.isCompareGridOpen()) {
			return;
		}
		this.drawerRef?.instance.closeDrawer();
	}

	updateDrawerSettings(): void {
		const deviceType = this.deviceService.getDeviceType();

		this.drawerRef.instance.size = this.setDeviceTypeAndDrawerWidth();
		this.drawerRef.instance.direction = deviceType === 'desktop' ? 'right' : 'bottom';
		this.drawerRef.instance.mobilePeek = deviceType !== 'desktop';
	}

	hasTheComparePaintedDoorTestBeenOpened(): boolean {
		if (isPlatformServer(this.platformId)) {
			return true;
		}

		// if the storage key does not exist, the feature should be shown
		const shouldTheCompareFeatureBeHidden: string =
			this.sessionStorageService.get(
				COMPARE_PRODUCT_LIST_HIDE_FEATURE_SESSION_STORAGE_KEY,
				''
			) || 'false';
		const hasThePaintedDoorTestBeenOpened = shouldTheCompareFeatureBeHidden === 'true';
		this.store.dispatch(
			setIsCompareFeatureHidden({ isCompareFeatureHidden: hasThePaintedDoorTestBeenOpened })
		);
		if (hasThePaintedDoorTestBeenOpened) {
			this.clearCompareList();
		}

		return hasThePaintedDoorTestBeenOpened;
	}

	getProductDetailsFromApi(ecodes: string[], isClearance: boolean): void {
		this.store.dispatch(setCompareSkeletonLoader({ isCompareSkeletonLoaderVisible: true }));

		//set the product details to state and loading state and error state
		this.productListCompareDao
			.getProductDetails(ecodes, isClearance)
			.pipe(take(1))
			.subscribe({
				next: (response: CompareProductDetails) => {
					this.store.dispatch(setCompareProductDetails({ productDetails: response }));
					this.store.dispatch(
						setCompareProductDetailsCallFailed({ hasProductDetailsCallFailed: false })
					);
					//TODO: This probably needs to be set after processing the data for the grid.
					this.store.dispatch(
						setCompareSkeletonLoader({ isCompareSkeletonLoaderVisible: false })
					);
				},
				error: () => {
					this.store.dispatch(
						setCompareProductDetailsCallFailed({ hasProductDetailsCallFailed: true })
					);
					this.store.dispatch(
						setCompareSkeletonLoader({ isCompareSkeletonLoaderVisible: false })
					);
				}
			});
	}

	removeProductFromCompareGrid(ecode: string): void {
		if (isPlatformServer(this.platformId)) {
			return;
		}
		const cplProducts = this.compareGridList().products;

		const updatedList = cplProducts.filter((product) => product.style.partNumber !== ecode);

		this.store.dispatch(
			setCompareProductDetails({
				productDetails: { ...this.compareGridList(), products: updatedList }
			})
		);
		this.store.dispatch(removeCompareProduct({ ecode }));

		if (!updatedList.length) {
			this.closeCompareGrid();
		}

		this.updateDrawerSettings();
	}

	modifyImgSrcWidthAndHeight(imgURL: string): string {
		const newImageParams = '?wid=355&hei=355&qlt=85,0&fmt=webp&op_sharpen=1';
		return imgURL.split('?')[0] + newImageParams;
	}

	coalesceAllProductAttributes(products: CompareProductItem[]): CompareProductItem[] {
		// Get all unique and sorted defining and descriptive attributes across all products
		const allSortedAttributesList = this.getAllUniqueAttributes(products);

		return products.map((product) => {
			// Extract the product's original defining attributes (or an empty array if missing)
			const originalDefining = product.style?.definingAttributes ?? [];

			// Extract only the facetable descriptive attributes from the product
			const originalDescriptive =
				product.style?.descriptiveAttributes.filter((attr) => attr.isFacetable) ?? [];

			// Coalesce the product's defining attributes against the full sorted list
			const coalescedDefining = this.coalesceAttributes(
				allSortedAttributesList.defining,
				originalDefining
			);

			// Coalesce the product's descriptive attributes against the full sorted list
			const coalescedDescriptive = this.coalesceAttributes(
				allSortedAttributesList.descriptive,
				originalDescriptive
			);

			// Return a new product object with updated, coalesced attributes
			return {
				...product,
				style: {
					...product.style,
					definingAttributes: coalescedDefining,
					descriptiveAttributes: coalescedDescriptive
				}
			};
		});
	}

	private coalesceAttributes(
		allSortedAttributes: CompareItemAttribute[],
		originalAttributes: CompareItemAttribute[]
	): CompareItemAttribute[] {
		// Create a map of original attributes for quick lookup by name
		const originalMap = new Map(originalAttributes.map((attr) => [attr.name, attr]));

		return allSortedAttributes.map((attr) => {
			const matched = originalMap.get(attr.name);
			// Start with the base attribute and Override with matched attribute if found Ensure value is a trimmed string or fallback to '-'
			return {
				// Start with the base attribute from allSortedAttributes
				...attr,
				//Override with matched attribute if found
				...(matched ?? {}),
				// If a match is found use its value, else default ('-')
				value: matched?.value?.toString().trim() || '-'
			};
		});
	}

	private getAllUniqueAttributes(products: CompareProductItem[]): {
		defining: CompareItemAttribute[];
		descriptive: CompareItemAttribute[];
	} {
		const definingAttrsMap = new Map<string, CompareItemAttribute>();
		const descriptiveAttrsMap = new Map<string, CompareItemAttribute>();

		for (const { style } of products) {
			// Add unique defining attributes
			style?.definingAttributes?.forEach((attr) => {
				if (!definingAttrsMap.has(attr.name)) {
					definingAttrsMap.set(attr.name, attr);
				}
			});

			// Add unique descriptive attributes that are facetable
			style?.descriptiveAttributes?.forEach((attr) => {
				if (attr.isFacetable && !descriptiveAttrsMap.has(attr.name)) {
					descriptiveAttrsMap.set(attr.name, attr);
				}
			});
		}

		// Sorting function to order attributes by sequence, pushing undefined to the end
		const sortBySequence = (a: CompareItemAttribute, b: CompareItemAttribute) =>
			(a.sequence ?? Number.MAX_SAFE_INTEGER) - (b.sequence ?? Number.MAX_SAFE_INTEGER);

		// Return sorted arrays of unique defining and descriptive attributes
		return {
			defining: [...definingAttrsMap.values()].sort(sortBySequence),
			descriptive: [...descriptiveAttrsMap.values()].sort(sortBySequence)
		};
	}

	private setDeviceTypeAndDrawerWidth(): 'large' | 'medium' | 'small' {
		if (isPlatformServer(this.platformId)) {
			return;
		}
		// If the user resizes or rotates their device, we need to maintain the hidden overflow
		const compareBarList = this.compareBarList$();

		const bp: BreakPointNew = this.deviceService.getDeviceBreakPointNew(false);

		if (bp <= BreakPointNew.MEDIUM) {
			// until 1024px
			return 'large';
		} else if (bp <= BreakPointNew.EXTRALARGE) {
			if (compareBarList.length <= 2) {
				return 'medium';
			} else {
				return 'large';
			}
		} else if (compareBarList.length <= 2) {
			return 'small';
		} else if (compareBarList.length === 3) {
			return 'medium';
		} else {
			return 'large';
		}
	}
}
